import { isFinite } from 'lodash';
import React from 'react';
import { Formik, FormikProps } from 'formik';

import { useUpdateEffect } from 'src/hooks';
import { BaseFormikForm } from './FormikForm';

export type FormikWizardProps<T> = FormikProps<T> & {
  children: React.ReactNode;
  onSubmit: (values: T, actions: any) => void;
  stepNumber?: number;
  withAutoFocus?: boolean;
  onStepChange?: (stepNumber: number) => void;
};

export const FormikWizard = <T extends {}>({
  children,
  onSubmit,
  initialValues,
  stepNumber,
  onStepChange,
  withAutoFocus,
  ...props
}: FormikWizardProps<T>) => {
  const [wizardStepNumber, setWizardStepNumber] = React.useState(stepNumber);

  useUpdateEffect(() => {
    if (isFinite(stepNumber)) {
      setWizardStepNumber(stepNumber as number);
    }
  }, [stepNumber]);

  useUpdateEffect(() => {
    if (onStepChange) {
      onStepChange(wizardStepNumber);
    }
  }, [onStepChange, wizardStepNumber]);

  const forms = React.Children.toArray(children).filter(Boolean);

  const activeForm = forms[wizardStepNumber];

  const goToNextStep = React.useCallback(() => {
    setWizardStepNumber(wizardStepNumber + 1);
  }, [wizardStepNumber]);

  const handleSubmit = React.useCallback(
    async (values, actions) => {
      if (activeForm.props.onSubmit) {
        await activeForm.props.onSubmit(values, actions);
      }
      const isLastStep = wizardStepNumber === forms.length - 1;
      if (isLastStep) {
        return onSubmit(values, actions);
      }
      // otherwise update values from page
      actions.setTouched({});
      actions.setSubmitting(false);
      goToNextStep();
    },
    [activeForm.props, forms.length, goToNextStep, onSubmit, wizardStepNumber]
  );

  return (
    <Formik<T>
      initialValues={initialValues}
      validationSchema={activeForm.props.validationSchema}
      onSubmit={handleSubmit}
      {...props}
    >
      {formikProps => (
        <BaseFormikForm withAutoFocus={withAutoFocus}>
          {React.cloneElement(activeForm, { ...formikProps, initialValues, goToNextStep })}
        </BaseFormikForm>
      )}
    </Formik>
  );
};

FormikWizard.defaultProps = {
  stepNumber: 0
};
